// AuthRedirect.tsx

import { useAuth0 } from './useAuth0';
import { useUser } from "./UserContext";
import {useBusinessContext} from "../context/BusinessContext";
import {findAllBusinessSummaryByOwnerId} from "../service/business/business-service";
import React, {useEffect, useRef} from "react";
const API_URL= process.env.REACT_APP_LOGIN_SERVICE_BASE_URL || '';

const LoadInitialData = () => {
    const { isAuthenticated, logout, isLoading, getAccessTokenSilently, error } = useAuth0();
    const { user, setUser } = useUser();
    const { setIsBusinessLoading, allBusiness, setAllBusiness, setSelectedBusiness } = useBusinessContext();
    // One-shot guard: the bootstrap (checkUser → findMyBusinesses) must run a
    // single time per session. Without it the effect's wide dependency array
    // re-fires the flow concurrently; when one of those concurrent runs hits the
    // catch/else it calls logout({returnTo}) which navigates the whole page away,
    // ABORTING the in-flight findMyBusinesses request (shows as "(cancelled)" in
    // the network tab and the business list never loads).
    const didInit = useRef(false);

    // console.log("LoadInitialData: ", user);
    useEffect(() => {
        const checkUserInBackend = async (token: string) => {
            try {
                setIsBusinessLoading(true);
                const response = await fetch(API_URL, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        query: `
                            query {
                                checkUser {
                                    id,
                                    email,
                                    firstName,
                                    lastName,
                                    active,
                                    image,
                                    lastLoginOn,
                                    language
                                }
                            }
                        `,
                    }),
                });

                // Non-200 (e.g. 404 from a wrong API URL, 502, brief outage) →
                // transient / misconfig. Do NOT logout — that was the "volta pro
                // login" bounce. Keep the session; a refresh retries.
                if (!response.ok) {
                    console.error("checkUser HTTP", response.status, "— sessão mantida");
                    setIsBusinessLoading(false);
                    return;
                }

                const result = await response.json();

                // Only a genuine AUTH error logs the user out. Any other GraphQL /
                // server error is treated as transient → keep the session.
                const errs = result?.errors as Array<{ message?: string }> | undefined;
                const isAuthError = Array.isArray(errs) && errs.some((e) =>
                    /unauthor|invalid token|not logged|forbidden|jwt|expired/i.test(e?.message || ""));
                if (isAuthError) {
                    console.error("checkUser auth error:", errs);
                    setUser(null);
                    await logout({ logoutParams: { returnTo: window.location.origin } });
                    return;
                }
                if (errs) {
                    console.error("checkUser returned errors (sessão mantida):", errs);
                    setIsBusinessLoading(false);
                    return;
                }

                if (result?.data?.checkUser?.active) {
                    setUser(result.data.checkUser); // Armazena o usuário no contexto
                    // Backend derives identity from the JWT — no ownerId/email passed.
                    const businessSummary = await findAllBusinessSummaryByOwnerId(token);
                    if (businessSummary) {
                        setAllBusiness(businessSummary);
                        // setSelectedBusiness(businessSummary.length > 0 ? businessSummary[0]:null)
                    }

                } else {
                    // logoutLocalStorage()
                    setUser(null);
                    await logout({
                        logoutParams: {
                            returnTo: window.location.origin,
                        },
                    });

                }
                setIsBusinessLoading(false);
            } catch (error) {
                // Network failure / non-JSON response → transient. Do NOT logout:
                // a brief API outage or misconfig shouldn't bounce the user to login.
                console.error("checkUser falhou (sessão mantida):", error);
                setIsBusinessLoading(false);
            }
        };

        const fetchInitialData = async () => {
            console.log("User authenticated. Getting extra info...");
            const token = await getAccessTokenSilently();
            await checkUserInBackend(token);
        };

        // Run the bootstrap exactly once, after Auth0 finishes and the user is
        // authenticated. The ref guard survives re-renders so concurrent runs
        // (and the page-aborting logout they could trigger) can't happen.
        if (!didInit.current && !isLoading && isAuthenticated && !user?.id && !allBusiness) {
            didInit.current = true;
            fetchInitialData();
        }
    }, [isLoading, isAuthenticated, getAccessTokenSilently, user?.id, allBusiness]);

    // Verifica se há um erro durante a autenticação
    if (error) {
        console.error(error)
    }

    return null;
};

export default LoadInitialData;
